/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import { ACTION, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { JSONTransformer, type JSONDocNode } from '@atlaskit/editor-json-transformer';
import { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import { ProseMirrorContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';
import { stripMatchingOuterQuotes } from '@atlassian/ai-model-io/utils/strip-matching-outer-quotes';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { copyPMFragmentToClipboard } from '@atlassian/editor-ai-common/utils/clipboard';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';
import {
	PreviewScreen,
	type PreviewButton,
	type RefineDropdownItem,
} from '@atlassian/generative-ai-modal/screens/Preview';

import type {
	CopyButtonClickAEP,
	EditPromptClickAEP,
	ResponseFeedbackButtonClickAEP,
	RetryPromptButtonClickAEP,
} from '../../../../types/analytics';
import type { BackendModel } from '../../../../types/prompt-request-types';
import type {
	ConvoAIResponseRovoAction,
	FeedbackMetadata,
	PluginFeedbackMetadata,
	RovoPublish,
} from '../../../../types/types';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { useAIUsageDisclaimer } from '../../../hooks/useAIUsageDisclaimer';
import { useCreateGenericChatActionPayload } from '../../../hooks/useCreateGenericChatActionPayload';
import { useLocalizedLatestPrompt } from '../../../hooks/useLocalizedLatestPrompt';
import { useOutsideClickHandler } from '../../../hooks/useOutsideClickHandler';
import { usePromptStatistics } from '../../../hooks/usePromptStatistics';
import {
	getAllEditorPluginAIConfigItems,
	getEditorPluginAIConfigItemActions,
	type EditorPluginAIConfigItem,
	type EditorPluginAIConfigItemAgentAction,
	type EditorPluginAIConfigItemGenericChatAction,
	type EditorPluginAIConfigItemMarkdownAction,
} from '../../../prebuilt/config-items/config-items';
import { isConfluenceTitleToolbarSuggestTitle } from '../../../prebuilt/config-items/utils/suggest-title';
import { convertExperienceName } from '../../../utils/analytics/analyticsFlow';
import type { ModelIO } from '../../../utils/analytics/analyticsFlowTypes';
import { useAnalyticsFlow } from '../../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
import { MAX_DIFF_RATIO, calculateDiffRatio } from '../../../utils/calculateDiffRatio';
import {
	convertADFResponseToPreview,
	convertPMFragmentToPreview,
} from '../../../utils/convert-markdown-to-preview';
import { shouldDiscardEndExperienceImmediately } from '../../../utils/discard-handling';
import { FAILURE_REASON } from '../../../utils/errors';
import { getPMFragmentWithFallback } from '../../../utils/get-pm-fragment-with-fallback';
import type { AIExperienceMachineContext } from '../../../utils/xstate/get-ai-experience-service';

type PreviewScreenWithLogicProps = {
	aiExperienceMachineContext: AIExperienceMachineContext;
	backendModel?: BackendModel;
	isInputActive: boolean;
	rovoAction?: ConvoAIResponseRovoAction['data'];
	rovoPublish?: RovoPublish;
};

export function PreviewScreenWithLogic({
	aiExperienceMachineContext,
	isInputActive,
	backendModel,
	rovoAction,
	rovoPublish,
}: PreviewScreenWithLogicProps) {
	const { formatMessage } = useIntl();

	const {
		isEmpty,
		parentPresetPromptLabel,
		latestPrompt,
		markdown: actualMarkdown,
		adfResponse,
		configItem,
		userInput: actualUserInput,
		userADFInput: actualUserADFInput,
		editCount,
		retryPromptCount,
		refinementCount,
		idMap,
		responseHistory: history,
		contextStatistics,
		promptTrigger,
		channelId,
		selectionMetaData,
	} = aiExperienceMachineContext;

	const aiExperienceCommonData = useAIExperienceCommonDataContext();
	const {
		appearance,
		editorPluginAIProvider,
		editorView,
		endExperience,
		positions,
		providerFactory,
		sendToAIExperienceMachine,
		lastTriggeredFrom,
		modalRef,
		triggeredFor,
		includeKnowledgeFromCurrentPage,
		canInlineStreamChanges,
	} = aiExperienceCommonData;

	const disclaimer = useAIUsageDisclaimer({ editorPluginAIProvider });

	/**
	 * Short title for the submenu if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	const childShortTitle =
		parentPresetPromptLabel && configItem.nestingConfig
			? formatMessage(configItem.nestingConfig.shortTitle)
			: '';

	const userInput = actualUserInput || '';
	const userADFInput = actualUserADFInput;

	const schema = editorView.state.schema;
	const product = editorPluginAIProvider.product;
	const adfStreamingEnabled = fg('platform_editor_ai_adf_prompts_in_all_products');
	/**
	 * Please check with Ethan/PM upon removing onExperienceEvent
	 * calls, to confirm it is no longer used in any ongoing experiments.
	 */
	useEffect(() => {
		editorPluginAIProvider.onExperienceEvent?.REVIEW_STATE_ENTERED();
	}, [editorPluginAIProvider]);
	const clearInputRef = useRef<() => boolean>();

	const lastHistoryEvent = history.entries[history.positionFromEnd];

	/**
	 * We may remove this diff ratio calculation, when we are sure that the backend
	 * calculation is working. We will need to have converted to the new ConvoAI endpoint.
	 * @see https://hello.jira.atlassian.cloud/browse/AIX-1665
	 * @see https://hello.jira.atlassian.cloud/browse/EDITOR-792
	 */
	const diffRatio =
		(lastHistoryEvent.modelInput.selection || lastHistoryEvent.modelInput.doc) &&
		actualMarkdown &&
		fg('editor_ai_no_improvement_deadend_fix')
			? calculateDiffRatio({
					input: lastHistoryEvent.modelInput.selection || lastHistoryEvent.modelInput.doc || '',
					output: actualMarkdown,
				})
			: 1;

	const responseTooSimilar = lastHistoryEvent.responseTooSimilar || diffRatio < MAX_DIFF_RATIO;
	const inputOutputDiffRatio = lastHistoryEvent.inputOutputDiffRatio;

	let markdown: string = actualMarkdown || '';
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const analyticsFlow = useAnalyticsFlow();
	const enqueuedPreviewStep = useRef<boolean>(false);
	const errorReported = React.useRef<boolean>(false);

	if (markdown && isConfluenceTitleToolbarSuggestTitle({ configItem, lastTriggeredFrom })) {
		markdown = stripMatchingOuterQuotes(markdown);
	}

	let previewToUse: JSX.Element | undefined;
	let pmFragment: Fragment = Fragment.empty;
	let pmFragmentPreviewData: ReturnType<typeof getPMFragmentWithFallback> | undefined;
	if (adfResponse && adfStreamingEnabled) {
		pmFragment = PMNode.fromJSON(schema, adfResponse).content;
		previewToUse = convertADFResponseToPreview({
			adfResponse,
			showTelepointer: false,
			providerFactory,
			schema,
		});
	} else {
		pmFragmentPreviewData = getPMFragmentWithFallback({
			markdown,
			schema,
			source: 'preview',
			fireAIAnalyticsEvent: (event) => {
				if (!errorReported.current) {
					errorReported.current = true;
					fireAIAnalyticsEvent(event);
				}
			},
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			idMap: idMap!,
		});
		pmFragment = pmFragmentPreviewData?.pmFragment || Fragment.empty;
		previewToUse = getPreviewElement(pmFragment);
	}

	const preview = previewToUse;

	useOutsideClickHandler({
		modalRef,
		userInput,
		onCancel: () => sendToAIExperienceMachine({ type: 'enable discard' }),
		onDiscard: () => sendToAIExperienceMachine({ type: 'enable discard' }),
	});

	/**
	 * gets preview JSX element based on fragment
	 */
	function getPreviewElement(pmFragment: Fragment): JSX.Element {
		if (responseTooSimilar) {
			const responseTooSimilarMessage = formatMessage(
				aiExperienceApplicationMessages.responseTooSimilarMessage,
			);
			return <Text as="p">{responseTooSimilarMessage}</Text>;
		}

		return convertPMFragmentToPreview({
			schema,
			pmFragment,
			showTelepointer: false,
			providerFactory,
		});
	}

	// preset prompt or base generate / interrogation
	const localizedLatestPrompt = useLocalizedLatestPrompt(latestPrompt);
	const promptStatistics = usePromptStatistics(localizedLatestPrompt);

	useEffect(() => {
		analyticsFlow.fireQueuedStep('preview');

		let modelIO: ModelIO | undefined;
		const stats = new ProseMirrorContentStatistics(pmFragment);
		const contentStatistics = stats.collectStatistics();
		if (adfResponse && adfStreamingEnabled) {
			modelIO = {
				selectionSize: lastHistoryEvent.modelInput.selection?.length,
				outputUnclosedTokens: {},
				outputHTMLConvertedToNodes: 0,
				outputHTMLConvertedToFallbackNodes: 0,
				isTextFallbackUsed: false,
				previewNodeCounts: contentStatistics.nodeCounts,
				previewMarkCounts: contentStatistics.markCounts,
				previewTotalCharacterCounts: contentStatistics.totalCharacterCount,
				userInputCharacterCount: promptStatistics?.totalCharacterCount,
				userInputNodeCounts: promptStatistics?.nodeCounts,
				userInputLinksCount: promptStatistics?.linksCount,
				userInputMentionsCount: promptStatistics?.mentionsCount,
			};
		} else if (pmFragmentPreviewData) {
			modelIO = {
				selectionSize: lastHistoryEvent.modelInput.selection?.length,
				nodesConvertedToHTMLInput: idMap ? Object.keys(idMap).length : undefined,
				outputUnclosedTokens: pmFragmentPreviewData.unclosedTokens,
				outputHTMLConvertedToNodes: pmFragmentPreviewData.htmlConvertedToNodes,
				outputHTMLConvertedToFallbackNodes: pmFragmentPreviewData.htmlConvertedToFallbackNodes,
				isTextFallbackUsed: pmFragmentPreviewData.isTextFallbackUsed,
				previewNodeCounts: contentStatistics.nodeCounts,
				previewMarkCounts: contentStatistics.markCounts,
				previewTotalCharacterCounts: contentStatistics.totalCharacterCount,
				userInputCharacterCount: promptStatistics?.totalCharacterCount,
				userInputNodeCounts: promptStatistics?.nodeCounts,
				userInputLinksCount: promptStatistics?.linksCount,
				userInputMentionsCount: promptStatistics?.mentionsCount,
			};
		}

		analyticsFlow.addAttributes({
			editCount,
			responseTooSimilar,
			modelIO: {
				contextStatistics,
				...modelIO,
			},
			inputOutputDiffRatio,
		});

		// Remove below flag when AIFC is fully rolled out in Jira, only required for old experience in Jira.
		if (fg('platform_editor_ai_streaming_ended_analytics_attr')) {
			analyticsFlow.updateCommonAttributes({ isStreamingEnded: true });
		}

		return () => {
			// We only want to enqueue the preview step if it hasn't been enqueued
			// already. This is to handle the case where the user clicks on an
			// ai action button where we manually enqueue the analytics event,
			// and then seperately fire a complete step.

			if (enqueuedPreviewStep.current === false) {
				analyticsFlow.enqueueStep({
					stepName: 'preview',
					attributes: undefined,
				});
			}
		};
		// expecting to enqueue step only once on unmount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fireDismissEvent = useCallback(() => {
		// Introduced in EDF-1443 to track Retry events as AI response dismissal
		fireAIAnalyticsEvent({
			payload: createUnifiedAnalyticsPayload({
				action: ACTION.DISMISSED,
				attributes: analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
				experienceName: convertExperienceName(configItem.key.toString(), lastTriggeredFrom),
				interactionID: channelId ?? analyticsFlow.getLastAiInteractionId(),
				invokedFrom: aiExperienceCommonData.lastTriggeredFrom,
				isProactive: false,
				isStreamingEnded: fg('platform_editor_ai_streaming_ended_analytics_attr')
					? analyticsFlow.getCommonAttributes().isStreamingEnded
					: undefined,
			}),
		});
	}, [
		analyticsFlow,
		aiExperienceCommonData.lastTriggeredFrom,
		channelId,
		configItem.key,
		fireAIAnalyticsEvent,
		lastTriggeredFrom,
	]);

	const fireResponseFeedbackEvent = (
		sentiment: FeedbackMetadata['sentiment'],
		productSuccessState: ResponseFeedbackButtonClickAEP['attributes']['productSuccessStatus'],
	) => {
		fireAIAnalyticsEvent({
			payload: {
				action: 'click',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'responseFeedbackButton',
				attributes: {
					sentiment,
					appearance,
					product,
					configItemTitle: configItem.agent?.name || configItem.key,
					lastTriggeredFrom,
					productSuccessStatus: productSuccessState,
					inputOutputDiffRatio,
					retryPromptCount,
					responseTooSimilar,
					aiInteractionID: analyticsFlow.getLastAiInteractionId(),
				},
				eventType: EVENT_TYPE.UI,
			} as ResponseFeedbackButtonClickAEP,
		});

		fireAIAnalyticsEvent({
			payload: createUnifiedAnalyticsPayload({
				action: ACTION.SUBMITTED,
				interactionID: channelId ?? analyticsFlow.getLastAiInteractionId(),
				experienceName: convertExperienceName(configItem.key.toString(), lastTriggeredFrom),
				isProactive: false,
				invokedFrom: aiExperienceCommonData.lastTriggeredFrom,
				attributes: {
					aiFeedbackResult: sentiment === 'good' ? 'up' : 'down',
					...analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
				},
			}),
		});
	};

	const handleFiringFeedbackEvent = async (feedbackMetadata: FeedbackMetadata) => {
		analyticsFlow.addAttributes({
			userFeedbackSentiment: feedbackMetadata.sentiment,
		});

		// we need to update the state machine to reflect the user feedback
		sendToAIExperienceMachine({
			type: 'feedback submitted',
			sentiment: feedbackMetadata.sentiment as 'good' | 'bad',
		});

		// handleFeedbackSubmission is intended to be implemented
		// by all products -- there will be a period where products won't have
		// it implemented (and will be relying on THUMBS_UP/THUMBS_DOWN)
		// in these cases -- we don't know the product response status
		if (!editorPluginAIProvider.handleFeedbackSubmission) {
			fireResponseFeedbackEvent(feedbackMetadata.sentiment, 'unknown');
			return;
		} else {
			try {
				const productResponse =
					await editorPluginAIProvider.handleFeedbackSubmission(feedbackMetadata);
				fireResponseFeedbackEvent(feedbackMetadata.sentiment, productResponse.status);
			} catch {
				fireResponseFeedbackEvent(feedbackMetadata.sentiment, 'failed');
			}
		}
	};

	const onEditPromptClick = useCallback(() => {
		const { userInput, userADFInput, presetTitle } = localizedLatestPrompt;

		analyticsFlow.addAttributes({
			actionTaken: 'edit-prompt',
		});

		fireAIAnalyticsEvent({
			payload: {
				action: 'click',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'editPromptButton',
				attributes: {
					experienceType: convertExperienceName(configItem.key.toString()),
					editCount,
				},
				eventType: EVENT_TYPE.UI,
			} as EditPromptClickAEP,
		});

		fireDismissEvent();

		sendToAIExperienceMachine({
			type: 'edit prompt',
			userInput,
			userADFInput,
			presetTitle,
		});
	}, [
		configItem.key,
		localizedLatestPrompt,
		analyticsFlow,
		fireAIAnalyticsEvent,
		editCount,
		fireDismissEvent,
		sendToAIExperienceMachine,
	]);

	const discardWithoutWarning = useCallback(() => {
		if (shouldDiscardEndExperienceImmediately(promptTrigger, history)) {
			endExperience();
			return;
		}
		const { presetTitle } = localizedLatestPrompt;

		sendToAIExperienceMachine({
			type: 'edit prompt',
			userInput: '',
			userADFInput: undefined,
			presetTitle,
		});
	}, [endExperience, history, localizedLatestPrompt, promptTrigger, sendToAIExperienceMachine]);

	const onBack = useCallback(() => {
		sendToAIExperienceMachine({
			type: 'enable discard',
			context: { onceDiscard: discardWithoutWarning },
		});
	}, [sendToAIExperienceMachine, discardWithoutWarning]);

	const aiExperienceMetadata = {
		configItemTitle: configItem.agent?.name || convertExperienceName(configItem.key.toString()),
		...(configItem.experienceId && fg('thor_support_google_docs_in_description_improver')
			? { experienceId: configItem.experienceId }
			: {}),
		lastTriggeredFrom,
		inputOutputDiffRatio,
		backendModel,
	};

	const getAIExperience: PluginFeedbackMetadata['getAIExperience'] = (
		hasUserConsent?: boolean,
	) => ({
		...aiExperienceMetadata,
		...(hasUserConsent && {
			userPrompt: localizedLatestPrompt.userInput,
			markdownResponse: markdown,
		}),
	});

	React.useEffect(() => {
		/**
		 * When userInput is empty, we need to clear interrogate editor
		 *  as setting empty userInput as defaultValue won't clear editor.
		 */
		if (!userInput) {
			clearInputRef.current?.();
		}
	}, [userInput]);

	const onInputChange = React.useCallback(
		(inputValue: string) => {
			sendToAIExperienceMachine({
				type: 'update user input',
				input: inputValue,
			});
		},
		[sendToAIExperienceMachine],
	);
	const onADFChange = React.useCallback(
		(inputValue: JSONDocNode) => {
			sendToAIExperienceMachine({
				type: 'update user input adf',
				adf: inputValue,
			});
		},
		[sendToAIExperienceMachine],
	);

	const disableInterrogation =
		configItem.disableInterrogation?.() ||
		editorPluginAIProvider.disableInterrogation ||
		history.positionFromEnd > 0;

	const onCancelClick = useCallback(() => {
		if (userInput) {
			fireDismissEvent();
			sendToAIExperienceMachine({ type: 'cancel refinement' });
		} else {
			fireDismissEvent();
			discardWithoutWarning();
		}
	}, [userInput, discardWithoutWarning, fireDismissEvent, sendToAIExperienceMachine]);

	const onRetryPromptClick = useCallback(() => {
		fireAIAnalyticsEvent({
			payload: {
				action: 'click',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'retryPromptButton',
				attributes: {
					experienceType: convertExperienceName(configItem.key.toString()),
				},
				eventType: EVENT_TYPE.UI,
			} as RetryPromptButtonClickAEP,
		});

		fireDismissEvent();

		sendToAIExperienceMachine({
			type: 'retry prompt',
			input: localizedLatestPrompt.userInput,
			adf: localizedLatestPrompt.userADFInput,
		});
	}, [
		configItem.key,
		fireAIAnalyticsEvent,
		fireDismissEvent,
		localizedLatestPrompt.userADFInput,
		localizedLatestPrompt.userInput,
		sendToAIExperienceMachine,
	]);

	const [hasClicked, setHasClicked] = React.useState(false);

	const onActionButtonClick = useCallback(
		({
			runFn,
			analyticsTitle,
			preserveEditorSelectionOnComplete,
		}: {
			analyticsTitle: string;
			preserveEditorSelectionOnComplete?: boolean;
			runFn: () => void;
		}) => {
			// We add this condition to ensure we don't run the action multiple times
			// if the user double-clicks quickly (e.g., on slower machines, the re-render
			// might not happen fast enough to disable the button before the second click).
			// Once hasClicked is true, subsequent clicks in the same component lifetime
			// will be ignored—unless we explicitly reset the state after an error, etc.
			if (!hasClicked) {
				try {
					setHasClicked(true);
					analyticsFlow.addAttributes({
						actionTaken: analyticsTitle,
					});
					analyticsFlow.enqueueStep({
						stepName: 'preview',
					});
					// We set this to true to prevent the preview step from being
					// enqueued again on unmount
					// Note -- in future -- we expect the complete step to be deprecated
					// an replaced with a "content highlighted" step
					enqueuedPreviewStep.current = true;
					analyticsFlow.fireQueuedStep('complete');
					const beforeActionRun = performance.now();
					runFn();
					analyticsFlow.enqueueStep({
						stepName: 'complete',
						attributes: {
							actionTaken: analyticsTitle,
							duration: Math.round(performance.now() - beforeActionRun),
							editCount: editCount,
							retryPromptCount,
							promptType: promptTrigger,
							refinementCount,
							inputOutputDiffRatio,
							includeKnowledgeFromCurrentPage,
						},
					});

					/**
					 * Please check with Ethan/PM upon removing onExperienceEvent
					 * calls, to confirm it is no longer used in any ongoing experiments.
					 */
					editorPluginAIProvider.onExperienceEvent?.EXPERIENCE_COMPLETE();
					endExperience({ preserveEditorSelectionOnComplete });
				} catch (error) {
					setHasClicked(false);
					fireAIAnalyticsEvent({
						payload: {
							action: 'unhandledErrorCaught',
							actionSubject: 'editorPluginAI',
							actionSubjectId: 'experienceApplication',
							attributes: {
								errorType: 'actionRunError',
								actionTitle: analyticsTitle,
								errorMessage: ErrorUtils.extractErrorMessage(error),
							},
							eventType: EVENT_TYPE.OPERATIONAL,
						},
					});
					sendToAIExperienceMachine({
						type: 'error',
						errorInfo: {
							failureReason: FAILURE_REASON.UNKNOWN,
						},
					});
				}
			}
		},
		[
			hasClicked,
			analyticsFlow,
			editCount,
			retryPromptCount,
			promptTrigger,
			refinementCount,
			inputOutputDiffRatio,
			includeKnowledgeFromCurrentPage,
			editorPluginAIProvider.onExperienceEvent,
			endExperience,
			fireAIAnalyticsEvent,
			sendToAIExperienceMachine,
		],
	);

	const mapMarkdownAction = useCallback(
		(action: EditorPluginAIConfigItemMarkdownAction): PreviewButton => {
			// For Rovo actions, we only generate a fragment from the suggested content (without bot pleasantries)
			const contentFragment =
				action.isRovoAgentAction && rovoAction
					? getPMFragmentWithFallback({
							markdown: rovoAction.content || '',
							schema,
							source: 'rovo-action',
							// TODO: EDF-1044 - fix markdownParser failed events
							fireAIAnalyticsEvent: () => {},
							// Won't be expecting Markdown Plus
							idMap: {},
						}).pmFragment
					: pmFragment;

			const { isDisabled, tooltip } = action.getDisabledState?.({ markdown, formatMessage }) ?? {};

			return {
				label: formatMessage(action.title),
				appearance: action.appearance,
				onClick: () => {
					onActionButtonClick({
						runFn: () => {
							action.run({
								editorView,
								positions,
								markdown,
								pmFragment: contentFragment,
								triggeredFor,
								selectionMetaData: adfStreamingEnabled ? selectionMetaData : undefined,
							});

							if (configItem.agent && editorPluginAIProvider.onDocChangeByAgent) {
								editorPluginAIProvider.onDocChangeByAgent(configItem.agent);
							}
						},
						analyticsTitle:
							typeof action.title.defaultMessage === 'string' ? action.title.defaultMessage : '',
					});
				},
				isDisabled: isDisabled || hasClicked,
				tooltip,
				icon: action.icon,
			};
		},
		[
			adfStreamingEnabled,
			rovoAction,
			schema,
			pmFragment,
			markdown,
			formatMessage,
			hasClicked,
			onActionButtonClick,
			editorView,
			positions,
			triggeredFor,
			configItem.agent,
			editorPluginAIProvider,
			selectionMetaData,
		],
	);

	const mapAgentAction = useCallback(
		(action: EditorPluginAIConfigItemAgentAction): PreviewButton => {
			return {
				label: formatMessage(action.title),
				appearance: action.appearance,
				icon: action.icon,
				shortcut: action.shortcut,
				onClick: () => {
					onActionButtonClick({
						runFn: () =>
							action.run({
								channelId,
								publish: rovoPublish,
							}),
						analyticsTitle:
							typeof action.title.defaultMessage === 'string' ? action.title.defaultMessage : '',
						preserveEditorSelectionOnComplete: action.preserveEditorSelectionOnComplete,
					});
				},
			};
		},
		[channelId, formatMessage, onActionButtonClick, rovoPublish],
	);

	const createGenericActionPayload = useCreateGenericChatActionPayload();
	const mapGenericChatAction = useCallback(
		({
			title,
			appearance,
			icon,
			shortcut,
			run,
			preserveEditorSelectionOnComplete,
		}: EditorPluginAIConfigItemGenericChatAction): PreviewButton => {
			return {
				label: formatMessage(title),
				appearance,
				icon,
				shortcut: shortcut,
				onClick: () => {
					onActionButtonClick({
						runFn: () =>
							run({
								publish: rovoPublish,
								...createGenericActionPayload(history),
							}),
						analyticsTitle: typeof title.defaultMessage === 'string' ? title.defaultMessage : '',
						preserveEditorSelectionOnComplete,
					});
				},
			};
		},
		[createGenericActionPayload, formatMessage, history, onActionButtonClick, rovoPublish],
	);

	// We need to use actions from the first config item in history,
	// because during refinement, the user might have selected a different
	// config item, that will have different actions.
	const firstConfigItemActions = getEditorPluginAIConfigItemActions({
		isEmpty,
		configItem: history.entries[0]?.configItem,
	});
	const applications = useMemo(() => {
		let actions = getEditorPluginAIConfigItemActions({ isEmpty, configItem });
		actions = firstConfigItemActions || actions;

		return actions.map((action) => {
			if (action.type === 'agent') {
				return mapAgentAction(action);
			}

			if (action.type === 'generic-chat') {
				return mapGenericChatAction(action);
			}

			return mapMarkdownAction(action);
		});
	}, [
		isEmpty,
		configItem,
		firstConfigItemActions,
		mapMarkdownAction,
		mapAgentAction,
		mapGenericChatAction,
	]);

	const refineDropdownItems = useMemo(() => {
		const allRefineSuggestions: EditorPluginAIConfigItem[] = getAllEditorPluginAIConfigItems({
			configItemWithOptions: editorPluginAIProvider.configItemWithOptions,
		}).filter((suggestion) => suggestion.showInRefineDropdown);

		const result: RefineDropdownItem[] = [];
		const parentForNesting: { [parentKey: string]: RefineDropdownItem } = {};

		const handleClickAnalytics = ({
			newConfigItem,
		}: {
			newConfigItem: (typeof allRefineSuggestions)[0];
		}) => {
			fireAIAnalyticsEvent({
				payload: {
					action: 'click',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'refinementOption',
					attributes: {
						experienceType: convertExperienceName(configItem.key.toString()),
						refinementType: convertExperienceName(newConfigItem.key.toString()),
					},
					eventType: EVENT_TYPE.UI,
				},
			});
		};

		const handleClick = (configItem: (typeof allRefineSuggestions)[0]) => () => {
			handleClickAnalytics({ newConfigItem: configItem });

			let latestPromptResponse = markdown;
			if (configItem.adfPrompt && adfStreamingEnabled) {
				if (adfResponse) {
					latestPromptResponse = JSON.stringify(adfResponse);
				} else {
					const { pmFragment } = getPMFragmentWithFallback({
						markdown,
						schema,
						source: 'markdown-to-adf-conversion-for-refinement',
						fireAIAnalyticsEvent: (event) => {
							if (!errorReported.current) {
								errorReported.current = true;
								fireAIAnalyticsEvent(event);
							}
						},
						// Ignored via go/ees005
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						idMap: idMap!,
					});
					const prosemirrorDocument = schema.nodes.doc.createAndFill({}, pmFragment);
					if (prosemirrorDocument) {
						const serializer = new JSONTransformer(schema);
						latestPromptResponse = JSON.stringify(
							serializer.encode(prosemirrorDocument, { keepNestedTables: true }),
						);
					}
				}
			}

			sendToAIExperienceMachine({
				type: 'refine',
				configItem,
				// Ignored via go/ees005
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				latestPromptResponse,
			});
		};

		for (const suggestion of allRefineSuggestions) {
			const { nestingConfig } = suggestion;
			if (!nestingConfig) {
				result.push({
					title: suggestion.title,
					icon: <>{suggestion.icon?.({ formatMessage })}</>,
					onClick: handleClick(suggestion),
				});
			} else {
				const parentKey = nestingConfig.parentTitle.id ?? JSON.stringify(nestingConfig.parentTitle);

				if (!parentForNesting[parentKey]) {
					const icon = nestingConfig.icon || suggestion.icon;

					const parent: RefineDropdownItem = {
						icon: <> {icon?.({ formatMessage })} </>,
						title: nestingConfig.parentTitle,
						children: [],
					};

					result.push(parent);
					parentForNesting[parentKey] = parent;
				}

				parentForNesting[parentKey].children?.push({
					title: suggestion.nestingConfig?.shortTitle ?? suggestion.title,
					onClick: handleClick(suggestion),
				});
			}
		}

		return result;
	}, [
		// Remove below 4 dependencies when all experiments for ADF prompts are removed
		adfStreamingEnabled,
		adfResponse,
		idMap,
		schema,
		configItem.key,
		editorPluginAIProvider.configItemWithOptions,
		fireAIAnalyticsEvent,
		formatMessage,
		markdown,
		sendToAIExperienceMachine,
	]);

	const refinementButtonClick = useCallback(() => {
		fireAIAnalyticsEvent({
			payload: {
				action: 'click',
				actionSubject: 'editorPluginAI',
				actionSubjectId: 'refinementButton',
				attributes: {
					experienceType: convertExperienceName(configItem.key.toString()),
				},
				eventType: EVENT_TYPE.UI,
			},
		});
	}, [configItem.key, fireAIAnalyticsEvent]);

	return (
		<PreviewScreen
			key={configItem.key}
			parentPresetPromptLabel={parentPresetPromptLabel}
			childShortTitle={childShortTitle}
			latestPrompt={localizedLatestPrompt}
			preview={preview}
			disableActions={!!responseTooSimilar}
			inputValue={userInput}
			inputADFValue={userADFInput}
			isInputActive={isInputActive}
			onCancel={onCancelClick}
			onInputChange={onInputChange}
			onADFChange={onADFChange}
			onInputFocus={() => {
				sendToAIExperienceMachine({ type: 'start refinement' });
			}}
			onInputBlur={() => {
				if (!userInput) {
					sendToAIExperienceMachine({ type: 'cancel refinement' });
				}
			}}
			onSubmit={() => {
				/**
				 * Do we really need to pass userInput and userADFInput
				 *  on firing interrogate event.
				 * We should have latest userInput and userADFInput
				 *  set by onInputChange and onADFChange.
				 */
				sendToAIExperienceMachine({
					type: 'interrogate',
					userInput: userInput,
					userADFInput,
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					latestPromptResponse: markdown!,
				});
			}}
			onCopy={() => {
				fireAIAnalyticsEvent({
					payload: {
						action: 'click',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'copyButton',
						attributes: {
							experienceType: convertExperienceName(configItem.key.toString()),
							editCount,
						},
						eventType: EVENT_TYPE.UI,
					} as CopyButtonClickAEP,
				});

				// Fire ACTION.ACTIONED event for copy action
				fireAIAnalyticsEvent({
					payload: createUnifiedAnalyticsPayload({
						action: ACTION.ACTIONED,
						interactionID: channelId ?? analyticsFlow.getLastAiInteractionId(),
						experienceName: convertExperienceName(configItem.key.toString()),
						isProactive: false,
						attributes: {
							...analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
							aiResultAction: 'copied',
						},
						invokedFrom: lastTriggeredFrom,
					}),
				});

				analyticsFlow.addAttributes({
					contentCopied: true,
				});
				copyPMFragmentToClipboard({ editorView, pmFragment });
			}}
			onRetry={!configItem.agent ? onRetryPromptClick : undefined}
			onGoodResponseButtonClick={() => {
				const feedbackMetadata = {
					sentiment: 'good' as const,
					getAIExperience,
					editorAttributes: { appearance, product },
				};

				// Now deprecated - migrate to handleFeedbackSubmission
				editorPluginAIProvider.onExperienceEvent &&
					editorPluginAIProvider.onExperienceEvent.THUMBS_UP();

				handleFiringFeedbackEvent(feedbackMetadata);
			}}
			onBadResponseButtonClick={() => {
				const feedbackMetadata = {
					sentiment: 'bad' as const,
					getAIExperience,
					editorAttributes: { appearance, product },
				};

				// Now deprecated - migrate to handleFeedbackSubmission
				editorPluginAIProvider.onExperienceEvent &&
					editorPluginAIProvider.onExperienceEvent.THUMBS_DOWN();

				handleFiringFeedbackEvent(feedbackMetadata);
			}}
			onEditPromptClick={onEditPromptClick}
			onBack={onBack}
			applications={applications}
			PromptEditor={editorPluginAIProvider.PromptEditor}
			clearInputRef={clearInputRef}
			disclaimer={disclaimer}
			providerFactory={providerFactory}
			schema={schema}
			disableInterrogation={disableInterrogation}
			agent={configItem.agent}
			onRefinementClick={refinementButtonClick}
			refineDropdownItems={refineDropdownItems}
			canInlineStreamChanges={canInlineStreamChanges()}
		/>
	);
}
